import { PhotoSubmissionStatusBadge } from '@/components/photo-submissions/PhotoSubmissionStatusBadge';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';

export type PhotoSubmissionStatus = 'pending' | 'approved' | 'rejected';

export interface PhotoSubmission {
  id: string;
  userId: string;
  entityType: string;
  entityId: string;
  mediaId: string;
  status: PhotoSubmissionStatus;
  wantsCredit: boolean;
  creditName?: string | null;
  termsAcceptedAt: string;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
  updatedAt: string;
  // Relations
  user?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
    imageUrl?: string | null;
  } | null;
  media?: {
    id: string;
    fileName: string;
    name?: string | null;
    fileType: string;
    url?: string;
  } | null;
  reviewer?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
  } | null;
  // Computed (from API)
  entityName?: string | null;
}

/**
 * Photo Submissions CRUD configuration
 */
export const photoSubmissionCrudConfig: CrudConfig<PhotoSubmission> = {
  entityName: 'Photo Submission',
  entityNamePlural: 'Photo Submissions',

  endpoints: {
    list: '/photo-submissions',
    single: (id: string) => `/photo-submissions/${id}`,
    create: '/photo-submissions', // Not used - created via mobile
    update: (id: string) => `/photo-submissions/${id}`,
    delete: (id: string) => `/photo-submissions/${id}`,
  },

  fields: [
    {
      key: 'status',
      label: 'Status',
      type: 'custom',
      format: (value: string) => (
        <PhotoSubmissionStatusBadge status={value as PhotoSubmissionStatus} />
      ),
    },
    {
      key: 'entityType',
      label: 'Type',
      type: 'text',
      format: (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : ''),
    },
    {
      key: 'entityId',
      label: 'Entity',
      type: 'custom',
      format: (_value: string, item?: PhotoSubmission) => {
        if (!item) return null;
        const entityName = item.entityName || item.entityId;
        const entityPath = item.entityType === 'attraction' ? 'attractions' : item.entityType;
        return (
          <Link to={`/${entityPath}/${item.entityId}`} className="text-primary hover:underline">
            {entityName}
          </Link>
        );
      },
    },
    {
      key: 'user',
      label: 'Submitted By',
      type: 'custom',
      format: (value: PhotoSubmission['user']) => {
        if (!value) return 'Unknown';
        const name = `${value.firstName || ''} ${value.lastName || ''}`.trim() || value.email;
        return name;
      },
    },
    {
      key: 'wantsCredit',
      label: 'Wants Credit',
      type: 'custom',
      format: (value: boolean) => (value ? 'Yes' : 'No'),
    },
    {
      key: 'creditName',
      label: 'Credit Name',
      type: 'text',
    },
    {
      key: 'createdAt',
      label: 'Submitted',
      type: 'datetime',
    },
    {
      key: 'reviewedAt',
      label: 'Reviewed',
      type: 'datetime',
    },
    {
      key: 'reviewer',
      label: 'Reviewed By',
      type: 'custom',
      format: (value: PhotoSubmission['reviewer']) => {
        if (!value) return '-';
        return `${value.firstName || ''} ${value.lastName || ''}`.trim() || value.email;
      },
    },
    {
      key: 'rejectionReason',
      label: 'Rejection Reason',
      type: 'text',
    },
    {
      key: 'mediaId',
      label: 'Photo',
      type: 'custom',
      format: (_value: string, item?: PhotoSubmission) => {
        const url = item?.media?.url;
        if (!url) return null;
        return (
          <img src={url} alt="Submitted photo" className="h-16 w-16 rounded-md object-cover" />
        );
      },
    },
  ],

  listView: {
    fields: ['mediaId', 'status', 'entityType', 'entityId', 'user', 'wantsCredit', 'createdAt'],
    searchableFields: ['creditName'],
    sortableFields: ['createdAt', 'updatedAt', 'status'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    itemsPerPage: 20,
    // Status is driven by the tabs on PhotoSubmissionsPage (which pass a
    // `defaultWhere`), so there's no in-list status filter to avoid two
    // competing status conditions being AND-ed together.
    actions: {
      view: { enabled: true },
      create: { enabled: false }, // Users create via mobile
      edit: { enabled: false }, // Review action instead
      delete: { enabled: false },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Submission Details',
        description: 'Photo and entity information',
        fields: ['status', 'entityType', 'entityId'],
        defaultExpanded: true,
      },
      {
        title: 'Submitter Information',
        description: 'User who submitted the photo',
        fields: ['user', 'wantsCredit', 'creditName'],
        defaultExpanded: true,
      },
      {
        title: 'Review Information',
        description: 'Review status and details',
        fields: ['reviewedAt', 'reviewer', 'rejectionReason'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [], // Not used - review workflow only
  },
};
